import { useCallback, useEffect, useRef, useState } from 'react';

// Pointer-driven list reordering, for touch as well as mouse: the handle
// captures the pointer, the dragged row follows it, and the rows it passes
// slide out of the way. Rows are assumed to be uniform height — true of the
// section rows — so the drop index is just the drag distance in whole rows.
//
// Rows opt in with a data-reorder-index attribute, which is what lets a fixed
// row (Recommended) sit in the same list without being counted.

// How long the released row takes to settle into its slot. The list only
// reorders once this has run, so it doubles as the commit delay.
const RELEASE_MS = 160;
const RELEASE_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function move(items, from, to) {
  const next = items.slice();
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}

export function useDragReorder(items, onChange) {
  const listRef = useRef(null);
  // Live geometry lives in a ref so a move does not depend on the render
  // that follows it; `drag` is the same thing, for drawing.
  const dragRef = useRef(null);
  const [drag, setDrag] = useState(null);
  // One frame with transitions off, so the reorder itself does not animate:
  // every row's DOM position changes and its transform clears at the same
  // moment, and those two cancel out only if neither is tweened.
  const [isSettling, setIsSettling] = useState(false);
  const releaseRef = useRef(null);

  useEffect(() => {
    if (!isSettling) return undefined;

    // Two frames: the first paints the settled state, the second releases the
    // rows back to being animatable.
    let inner;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setIsSettling(false));
    });

    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [isSettling]);

  const flushRelease = useCallback(() => {
    const pending = releaseRef.current;
    if (!pending) return;

    clearTimeout(pending.timer);
    releaseRef.current = null;
    pending.commit();
  }, []);

  // On unmount the draft is discarded with the sheet, so drop the pending
  // commit rather than flushing it into a component that is going away.
  useEffect(() => () => clearTimeout(releaseRef.current?.timer), []);

  const start = (event, index) => {
    // Primary button only; touch and pen report 0 as well.
    if (event.button !== 0) return;

    // A drag starting mid-settle takes the previous one as already landed.
    flushRelease();

    const rows = listRef.current?.querySelectorAll('[data-reorder-index]') ?? [];
    if (rows.length < 2) return;

    // Row pitch, gap included, measured from the first pair.
    const step =
      rows[1].getBoundingClientRect().top - rows[0].getBoundingClientRect().top;
    if (!step) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    const next = {
      from: index,
      to: index,
      startY: event.clientY,
      offset: 0,
      step,
      isReleasing: false,
    };
    dragRef.current = next;
    setDrag(next);
  };

  const onMove = (event) => {
    const state = dragRef.current;
    if (!state) return;

    const offset = event.clientY - state.startY;
    const to = clamp(
      state.from + Math.round(offset / state.step),
      0,
      items.length - 1,
    );

    dragRef.current = { ...state, offset, to };
    setDrag(dragRef.current);
  };

  const end = () => {
    const state = dragRef.current;
    if (!state) return;
    dragRef.current = null;

    // Animate to the slot the row will occupy, then commit. Because the
    // offset lands exactly on the slot, clearing it after the reorder is a
    // no-op on screen and the row simply stays where it came to rest.
    setDrag({
      ...state,
      offset: (state.to - state.from) * state.step,
      isReleasing: true,
    });

    const commit = () => {
      setDrag(null);
      setIsSettling(true);
      if (state.to !== state.from) onChange(move(items, state.from, state.to));
    };

    releaseRef.current = {
      commit,
      timer: setTimeout(() => {
        releaseRef.current = null;
        commit();
      }, RELEASE_MS),
    };
  };

  const cancel = () => {
    dragRef.current = null;
    setDrag(null);
  };

  // Dragging is unusable without a pointer, so the handle doubles as an
  // arrow-key control.
  const onKeyDown = (event, index) => {
    const delta =
      event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
    if (!delta) return;

    event.preventDefault();
    const to = clamp(index + delta, 0, items.length - 1);
    if (to !== index) onChange(move(items, index, to));
  };

  const handleProps = (index) => ({
    onPointerDown: (event) => start(event, index),
    onPointerMove: onMove,
    onPointerUp: end,
    onPointerCancel: cancel,
    onKeyDown: (event) => onKeyDown(event, index),
  });

  // The dragged row tracks the pointer; every row it has passed shifts one
  // place the other way to open the gap it will land in.
  const rowStyle = useCallback(
    (index) => {
      if (isSettling) return { transition: 'none' };
      if (!drag) return undefined;

      const { from, to, offset, step, isReleasing } = drag;

      if (index === from) {
        return {
          transform: `translateY(${offset}px)`,
          // Following the pointer must not lag; landing should.
          transition: isReleasing
            ? `transform ${RELEASE_MS}ms ${RELEASE_EASING}`
            : 'none',
        };
      }

      if (from < to && index > from && index <= to) {
        return { transform: `translateY(${-step}px)` };
      }
      if (from > to && index < from && index >= to) {
        return { transform: `translateY(${step}px)` };
      }
      return undefined;
    },
    [drag, isSettling],
  );

  return { listRef, dragIndex: drag?.from ?? null, handleProps, rowStyle };
}
